import type { EnrichedSection } from '../types/enriched-section.type';

interface Counter {
  next: number;
}

// A numbered section (titled, not virtual) takes the next number of its level
// and opens a level for its sub-sections; the sub-sections of an unnumbered
// one carry on the numbering of its level.
const renumber = (
  sections: readonly EnrichedSection[],
  levelPath: readonly number[],
  counter: Counter,
): readonly EnrichedSection[] =>
  sections.map((section) => {
    if (!section.displayPath) {
      return {
        ...section,
        subSections: renumber(section.subSections, levelPath, counter),
      };
    }

    // eslint-disable-next-line functional/immutable-data
    const displayPath = [...levelPath, counter.next++];

    return {
      ...section,
      displayPath,
      subSections: renumber(section.subSections, displayPath, { next: 0 }),
    };
  });

const removeHidden = (
  sections: readonly EnrichedSection[],
  isDisplayed: (section: Readonly<EnrichedSection>) => boolean,
): readonly EnrichedSection[] =>
  sections.filter(isDisplayed).map((section) => ({
    ...section,
    subSections: removeHidden(section.subSections, isDisplayed),
  }));

// The sections whose display condition is false are left out, with their
// sub-sections, the others being numbered again without them.
export const withDisplayedSections = (
  virtualRoot: Readonly<EnrichedSection>,
  isDisplayed: (section: Readonly<EnrichedSection>) => boolean,
): EnrichedSection => ({
  ...virtualRoot,
  subSections: renumber(
    removeHidden(virtualRoot.subSections, isDisplayed),
    [],
    { next: 0 },
  ),
});
